import { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  BookOpen,
  Check,
  CheckCircle,
  Edit,
  FileText,
  MoreHorizontal,
  Package,
  Pencil,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCancelPurchaseOrder,
  useCreateReception,
  usePurchaseOrder,
  usePurchaseOrderReceptions,
  useUpdatePurchaseOrder,
  useUpdatePurchaseOrderStatus,
} from "~/hooks/purchasing";
import { handleAndNotify } from "~/lib/errorHandler";
import { useProfileStore } from "~/store/profileStore";
import type {
  Plant,
  PurchaseOrderFormData,
  PurchaseOrderItem,
  ReceptionItemFormData,
} from "~/types/purchasing";
import {
  CURRENCY_SYMBOLS,
  formatIlaRate,
  ITEM_TYPE_LABELS,
  ORDER_TYPE_LABELS,
  PURCHASE_ORDER_STATUS_COLORS,
  PURCHASE_ORDER_STATUS_LABELS,
  resolveDiscountAmount,
  resolveLineAmounts,
  UNIT_LABELS,
} from "~/types/purchasing";
import { generatePurchaseOrderPdf } from "~/utils/purchaseOrderPdf";

import { EditReceptionPlantDialog } from "./components/EditReceptionPlantDialog";
import { PurchaseOrderForm } from "./components/PurchaseOrderForm";
import { ReceptionDialog } from "./components/ReceptionDialog";
import { RequestReceptionModificationDialog } from "./components/RequestReceptionModificationDialog";

export function PurchaseOrderDetailPage() {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = params.id;

  const isNew = orderId === "new";
  const isEditMode =
    new URLSearchParams(location.search).get("edit") === "true";

  const [showEditForm, setShowEditForm] = useState(isNew || isEditMode);
  const [statusAction, setStatusAction] = useState<{
    action: "cancel" | "approve";
    open: boolean;
  }>({ action: "cancel", open: false });
  const [showReceptionDialog, setShowReceptionDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [editPlantReceptionId, setEditPlantReceptionId] = useState<
    string | null
  >(null);
  const [modifyRequest, setModifyRequest] = useState<{
    item: {
      id: string;
      name: string;
      unit: string;
      quantity: number;
      unitCost: number;
      expiry: string | null;
    };
    mode: "edit" | "delete";
  } | null>(null);

  const { data: order, isLoading } = usePurchaseOrder(
    isNew ? undefined : orderId
  );
  const { data: receptions } = usePurchaseOrderReceptions(
    isNew ? undefined : orderId
  );
  const updateOrder = useUpdatePurchaseOrder();
  const createReception = useCreateReception();
  const cancelOrder = useCancelPurchaseOrder();
  const updateStatus = useUpdatePurchaseOrderStatus();
  const role = useProfileStore((s) => s.role);

  useEffect(() => {
    if (isEditMode) {
      setShowEditForm(true);
    }
  }, [isEditMode]);

  const handleSubmit = async (data: PurchaseOrderFormData) => {
    try {
      if (isNew) {
        // For new orders, we need to use createPurchaseOrder hook
        // But this page is for detail view. New order should redirect to form
        toast.error("Use la página de nueva orden");
        return;
      }

      await updateOrder.mutateAsync({ id: orderId!, data });
      toast.success("Orden de compra actualizada");
      setShowEditForm(false);
    } catch (error) {
      handleAndNotify(error, {
        userMessage: "Error al actualizar la orden de compra",
        context: "handleSaveOrder",
        component: "PurchaseOrderDetailPage",
        metadata: { orderId: orderId! },
      });
    }
  };

  const handleStatusAction = async () => {
    if (!order) return;

    try {
      if (statusAction.action === "approve") {
        // "approved" is surfaced via the "sent" DB status (see
        // PURCHASE_ORDER_STATUS_LABELS); no receptions are required.
        await updateStatus.mutateAsync({ id: order.id, status: "sent" });
        toast.success("Orden aprobada");
      } else {
        await cancelOrder.mutateAsync(order.id);
        toast.success("Orden cancelada");
      }
      setStatusAction({ ...statusAction, open: false });
    } catch (error) {
      handleAndNotify(error, {
        userMessage: "Error al actualizar el estado",
        context: "handleStatusAction",
        component: "PurchaseOrderDetailPage",
        metadata: { orderId: order.id },
      });
    }
  };

  const handleReceiveItems = async (
    items: ReceptionItemFormData[],
    plant: Plant,
    observations?: string
  ) => {
    if (!order) return;

    try {
      await createReception.mutateAsync({
        purchase_order_id: order.id,
        reception_date: dayjs().format("YYYY-MM-DD"),
        plant,
        observations,
        items,
      });
      toast.success(`Recepción registrada en ${plant}. Stock actualizado.`);
      setShowReceptionDialog(false);
    } catch (error) {
      handleAndNotify(error, {
        userMessage: "Error al registrar la recepción",
        context: "handleReceiveItems",
        component: "PurchaseOrderDetailPage",
        metadata: {
          orderId: order.id,
          plant,
          itemCount: items.length,
        },
      });
    }
  };

  const handleCompleteReception = async () => {
    if (!order) return;

    try {
      await updateStatus.mutateAsync({ id: order.id, status: "received" });
      toast.success("Orden de compra marcada como recibida");
      setShowCompleteDialog(false);
    } catch (error) {
      handleAndNotify(error, {
        userMessage: "Error al finalizar la recepción",
        context: "handleCompleteReception",
        component: "PurchaseOrderDetailPage",
        metadata: { orderId: order.id },
      });
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    const symbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS];
    const fractionDigits = currency === "USD" ? 2 : 0;
    return `${symbol}${new Intl.NumberFormat("es-CL", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value)}`;
  };

  if (isNew) {
    navigate("/purchasing/orders");
    return null;
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </PageContainer>
    );
  }

  if (!order) {
    return (
      <PageContainer className="text-center">
        <p className="text-muted-foreground">Orden no encontrada</p>
        <Button variant="link" onClick={() => navigate("/purchasing/orders")}>
          Volver a órdenes
        </Button>
      </PageContainer>
    );
  }

  const canEdit = order.status === "draft";
  const canReceive =
    order.status === "draft" ||
    order.status === "sent" ||
    order.status === "partial";
  const canCancel = order.status !== "cancelled" && order.status !== "received";
  const canComplete = order.status === "partial";

  // Accounting entry (apunte contable) posted at reception — only test-company
  // receptions carry one. The target page (/contabilidad/movimientos/:id) is
  // admin/superadmin-only, so gate the links on role too. receptions come
  // ordered by reception_date desc, so the first with an asiento is the latest.
  const canViewAsiento = role === "superadmin" || role === "admin";
  const latestReceptionAsientoId =
    receptions?.find((r) => r.account_movement_id)?.account_movement_id ?? null;

  // Compute reception summary for the complete dialog
  const receptionSummary = order.items?.map((item) => ({
    id: item.id,
    description:
      item.ingredient_type === "product" && item.product
        ? item.product.short_name
        : item.description,
    ordered: item.quantity,
    received: item.received_quantity || 0,
    isComplete: (item.received_quantity || 0) >= item.quantity,
  }));
  const hasPartialItems = receptionSummary?.some((item) => !item.isComplete);
  const totalItems = receptionSummary?.length || 0;
  const completeItems =
    receptionSummary?.filter((item) => item.isComplete).length || 0;

  // Build a map of PO item ID -> weighted average actual_unit_cost from receptions
  const receptionCostMap = new Map<
    string,
    { avgCost: number; totalQty: number }
  >();
  if (receptions && receptions.length > 0) {
    for (const reception of receptions) {
      for (const item of reception.items || []) {
        const existing = receptionCostMap.get(item.purchase_order_item_id);
        if (existing) {
          // Weighted average: (existing.avgCost * existing.totalQty + item.actual_unit_cost * item.quantity_received) / newTotalQty
          const newTotalQty = existing.totalQty + item.quantity_received;
          const weightedAvg =
            (existing.avgCost * existing.totalQty +
              item.actual_unit_cost * item.quantity_received) /
            newTotalQty;
          receptionCostMap.set(item.purchase_order_item_id, {
            avgCost: weightedAvg,
            totalQty: newTotalQty,
          });
        } else {
          receptionCostMap.set(item.purchase_order_item_id, {
            avgCost: item.actual_unit_cost,
            totalQty: item.quantity_received,
          });
        }
      }
    }
  }

  // Derive Resumen totals from items so over-receipts (received > ordered) are
  // reflected immediately, since order.subtotal/tax_amount/total are frozen at
  // PO creation and never re-synced.
  // Net line total = gross (effective qty * price) minus the per-line discount, re-resolved on
  // the effective gross so over-receipts/variable costs move it consistently (same as the form).
  const computedLineNet = (it: PurchaseOrderItem) => {
    const recv = it.received_quantity || 0;
    const qty = Math.max(it.quantity, recv);
    const data = receptionCostMap.get(it.id);
    const price = data ? data.avgCost : it.unit_price;
    return resolveLineAmounts(qty, price, it.discount_type, it.discount_value)
      .net;
  };
  const computedSubtotal = (order.items ?? []).reduce(
    (acc, it) => acc + computedLineNet(it),
    0
  );
  // Order-level discount comes off the neto before IVA; recompute it from the derived subtotal
  // so over-receipts move the discount (and therefore the IVA base) consistently.
  const computedDiscount = resolveDiscountAmount(
    computedSubtotal,
    order.discount_type,
    order.discount_value
  );
  // Exento de IVA: a VAT-exempt OC adds no 19% IVA.
  const computedTax = order.is_exento
    ? 0
    : (computedSubtotal - computedDiscount) * 0.19;
  // ILA is a per-line additional tax (after IVA); sum each line's rounded amount on its net.
  const computedIla = (order.items ?? []).reduce(
    (acc, it) => acc + Math.round((computedLineNet(it) * (it.ila_rate ?? 0)) / 100),
    0
  );
  const computedTotal =
    computedSubtotal - computedDiscount + computedTax + computedIla;

  if (showEditForm && canEdit) {
    const formData: PurchaseOrderFormData = {
      supplier_id: order.supplier_id,
      company_id: order.company_id,
      order_type: order.order_type,
      issue_date: order.issue_date,
      delivery_date: order.delivery_date || "",
      currency: order.currency,
      exchange_rate_id: order.exchange_rate_id || undefined,
      payment_terms: order.payment_terms || "",
      observations: order.observations || "",
      discount_type: order.discount_type ?? "none",
      discount_value: order.discount_value ?? 0,
      is_exento: order.is_exento ?? false,
      items:
        order.items?.map((item) => ({
          id: item.id,
          ingredient_type: item.ingredient_type,
          oil_type_id: item.oil_type_id || undefined,
          raw_material_id: item.raw_material_id || undefined,
          product_id: item.product_id || undefined,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          discount_type: item.discount_type ?? "none",
          discount_value: item.discount_value ?? 0,
          ila_rate: item.ila_rate,
        })) || [],
    };

    // Items with receptions cannot be deleted
    const protectedItemIds = new Set(
      order.items
        ?.filter((item) => item.received_quantity > 0)
        .map((item) => item.id) ?? []
    );

    return (
      <PageContainer>
        <PageHeader
          title={`Editar OC #${String(order.po_number).padStart(5, "0")}`}
          back={
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-9 text-muted-foreground"
              onClick={() => setShowEditForm(false)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver al detalle
            </Button>
          }
        />
        <PurchaseOrderForm
          orderType={order.order_type}
          initialData={formData}
          onSubmit={handleSubmit}
          isSubmitting={updateOrder.isPending}
          protectedItemIds={protectedItemIds}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={`Orden de Compra #${String(order.po_number).padStart(5, "0")}`}
        description={dayjs(order.issue_date).format("DD/MM/YYYY")}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/purchasing/orders")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={async () => await generatePurchaseOrderPdf(order)}
            >
              <FileText className="mr-2 h-4 w-4" />
              Ver PDF
            </Button>
            {canViewAsiento && latestReceptionAsientoId && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    `/contabilidad/movimientos/${latestReceptionAsientoId}`
                  )
                }
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Apunte contable
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" onClick={() => setShowEditForm(true)}>
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </Button>
            )}
            {order.status === "draft" && (
              <Button
                onClick={() =>
                  setStatusAction({ action: "approve", open: true })
                }
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Aprobar
              </Button>
            )}
            {canReceive && (
              <Button onClick={() => setShowReceptionDialog(true)}>
                <Package className="mr-2 h-4 w-4" />
                {order.status === "partial"
                  ? "Continuar Recepción"
                  : "Registrar Recepción"}
              </Button>
            )}
            {canComplete && (
              <Button
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setShowCompleteDialog(true)}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Finalizar Recepción
              </Button>
            )}
            {canCancel && (
              <Button
                variant="destructive"
                onClick={() =>
                  setStatusAction({ action: "cancel", open: true })
                }
              >
                <XCircle className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Información General</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Proveedor</p>
              <p className="font-medium">{order.supplier?.name || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Estado</p>
              <Badge
                variant="outline"
                className={PURCHASE_ORDER_STATUS_COLORS[order.status]}
              >
                {PURCHASE_ORDER_STATUS_LABELS[order.status]}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tipo de Orden</p>
              <Badge
                variant="outline"
                className={
                  order.order_type === "products"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-amber-100 text-amber-800"
                }
              >
                {ORDER_TYPE_LABELS[order.order_type]}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Moneda</p>
              <p className="font-medium">{order.currency}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Condiciones de Pago
              </p>
              <p className="font-medium">{order.payment_terms || "-"}</p>
            </div>
            {order.received_at && (
              <div>
                <p className="text-sm text-muted-foreground">
                  Fecha de Recepción
                </p>
                <p className="font-medium">
                  {dayjs(order.received_at).format("DD/MM/YYYY HH:mm")}
                </p>
              </div>
            )}
            {order.observations && (
              <div className="col-span-full">
                <p className="text-sm text-muted-foreground">Observaciones</p>
                <p className="font-medium">{order.observations}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resumen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">
                {formatCurrency(computedSubtotal, order.currency)}
              </span>
            </div>
            {computedDiscount > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {order.discount_type === "percentage"
                    ? `Descuento (${formatIlaRate(order.discount_value)})`
                    : "Descuento"}
                </span>
                <span className="font-mono text-red-700">
                  -{formatCurrency(computedDiscount, order.currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {order.is_exento ? "IVA (exenta)" : "IVA (19%)"}
              </span>
              <span className="font-mono">
                {formatCurrency(computedTax, order.currency)}
              </span>
            </div>
            {computedIla > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">ILA</span>
                <span className="font-mono">
                  {formatCurrency(computedIla, order.currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t pt-4 text-lg font-bold">
              <span>Total</span>
              <span className="font-mono">
                {formatCurrency(computedTotal, order.currency)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {order.order_type === "products" ? "Productos" : "Items"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead>Recibido</TableHead>
                <TableHead>Unidad</TableHead>
                <TableHead className="text-right">Precio Unit.</TableHead>
                <TableHead className="text-right">Descuento</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.items?.map((item, index) => {
                const received = item.received_quantity || 0;
                const progress = (received / item.quantity) * 100;
                const isComplete = received >= item.quantity;

                // Use reception cost if available, otherwise use original PO price
                const receptionData = receptionCostMap.get(item.id);
                const displayUnitPrice = receptionData
                  ? receptionData.avgCost
                  : item.unit_price;
                const effectiveQty = Math.max(item.quantity, received);
                // Net line = gross − per-line discount (re-resolved on the effective gross).
                const lineAmounts = resolveLineAmounts(
                  effectiveQty,
                  displayUnitPrice,
                  item.discount_type,
                  item.discount_value
                );
                const displaySubtotal = lineAmounts.net;

                return (
                  <TableRow
                    key={item.id}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  >
                    <TableCell>
                      <Badge variant="secondary">
                        {ITEM_TYPE_LABELS[item.ingredient_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.ingredient_type === "product" && item.product
                        ? item.product.short_name
                        : item.description}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.quantity.toLocaleString("es-CL")}
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-[120px] items-center gap-2">
                        <Progress value={progress} className="h-2 flex-1" />
                        <span
                          className={`whitespace-nowrap font-mono text-xs ${
                            isComplete
                              ? "text-green-600"
                              : "text-muted-foreground"
                          }`}
                        >
                          {received.toLocaleString("es-CL")}/
                          {item.quantity.toLocaleString("es-CL")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{UNIT_LABELS[item.unit] || item.unit}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(displayUnitPrice, order.currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {lineAmounts.discount > 0 ? (
                        <span className="text-red-700">
                          -{formatCurrency(lineAmounts.discount, order.currency)}
                          {item.discount_type === "percentage"
                            ? ` (${formatIlaRate(item.discount_value)})`
                            : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(displaySubtotal, order.currency)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reception History */}
      {receptions && receptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Historial de Recepciones</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {receptions.map((reception) => (
              <div
                key={reception.id}
                className="space-y-3 rounded-lg border p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      Recepción del{" "}
                      {dayjs(reception.reception_date).format("DD/MM/YYYY")}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {reception.received_by_profile && (
                        <span>Por: {reception.received_by_profile.name}</span>
                      )}
                      <span>·</span>
                      <span>Planta: {reception.plant}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => setEditPlantReceptionId(reception.id)}
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        Editar planta
                      </Button>
                      {canViewAsiento && reception.account_movement_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() =>
                            navigate(
                              `/contabilidad/movimientos/${reception.account_movement_id}`
                            )
                          }
                        >
                          <BookOpen className="mr-1 h-3 w-3" />
                          Apunte contable
                        </Button>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-green-100 text-green-800"
                  >
                    {reception.items?.reduce(
                      (sum, item) => sum + item.quantity_received,
                      0
                    ) || 0}{" "}
                    unidades
                  </Badge>
                </div>
                {reception.observations && (
                  <p className="text-sm text-muted-foreground">
                    {reception.observations}
                  </p>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead className="text-right">Costo Unit.</TableHead>
                      <TableHead>Vencimiento</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reception.items?.map((item, index) => (
                      <TableRow
                        key={item.id}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                      >
                        <TableCell>
                          {item.purchase_order_item?.description || "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.quantity_received.toLocaleString("es-CL")}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(
                            item.actual_unit_cost,
                            order.currency
                          )}
                        </TableCell>
                        <TableCell>
                          {item.expiry_date
                            ? dayjs(item.expiry_date).format("DD/MM/YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Acciones de la línea"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  setModifyRequest({
                                    item: {
                                      id: item.id,
                                      name:
                                        item.purchase_order_item?.description ||
                                        "Ítem",
                                      unit: item.purchase_order_item?.unit || "",
                                      quantity: item.quantity_received,
                                      unitCost: item.actual_unit_cost,
                                      expiry: item.expiry_date ?? null,
                                    },
                                    mode: "edit",
                                  })
                                }
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Solicitar edición
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() =>
                                  setModifyRequest({
                                    item: {
                                      id: item.id,
                                      name:
                                        item.purchase_order_item?.description ||
                                        "Ítem",
                                      unit: item.purchase_order_item?.unit || "",
                                      quantity: item.quantity_received,
                                      unitCost: item.actual_unit_cost,
                                      expiry: item.expiry_date ?? null,
                                    },
                                    mode: "delete",
                                  })
                                }
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Solicitar eliminación
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <AlertDialog
        open={statusAction.open}
        onOpenChange={(open) => setStatusAction({ ...statusAction, open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusAction.action === "approve"
                ? "Aprobar orden de compra"
                : "Cancelar Orden de Compra"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusAction.action === "approve"
                ? "La orden pasará a estado “Aprobado” y ya no se podrá editar. Podrás seguir registrando recepciones."
                : "¿Estás seguro de que deseas cancelar esta orden? Esta acción no se puede deshacer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            {statusAction.action === "approve" ? (
              <AlertDialogAction onClick={handleStatusAction}>
                Aprobar
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={handleStatusAction}
                className="bg-red-600 hover:bg-red-700"
              >
                Cancelar Orden
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Complete Reception Confirmation */}
      <AlertDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar Recepción</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Resumen de recepción para OC #
                  {String(order.po_number).padStart(5, "0")}
                </p>

                {hasPartialItems && (
                  <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-amber-800">
                    <XCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">
                      Algunos items no fueron recibidos completamente (
                      {completeItems}/{totalItems} completos)
                    </span>
                  </div>
                )}

                <div className="max-h-64 overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="text-right">Ordenado</TableHead>
                        <TableHead className="text-right">Recibido</TableHead>
                        <TableHead className="w-16 text-center">
                          Estado
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receptionSummary?.map((item, index) => (
                        <TableRow
                          key={item.id}
                          className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                          style={{
                            animationDelay: `${Math.min(index * 40, 400)}ms`,
                          }}
                        >
                          <TableCell className="max-w-[200px] truncate">
                            {item.description}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {item.ordered.toLocaleString("es-CL")}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {item.received.toLocaleString("es-CL")}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.isComplete ? (
                              <Check className="mx-auto h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="mx-auto h-5 w-5 text-amber-500" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <p className="text-sm text-muted-foreground">
                  Al finalizar, no se podrán registrar más recepciones para esta
                  orden.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCompleteReception}
              className="bg-green-600 hover:bg-green-700"
              disabled={updateStatus.isPending}
            >
              {updateStatus.isPending ? "Finalizando..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {order.items && (
        <ReceptionDialog
          open={showReceptionDialog}
          onOpenChange={setShowReceptionDialog}
          items={order.items}
          currency={order.currency}
          purchaseOrderId={order.id}
          onSubmit={handleReceiveItems}
          isLoading={createReception.isPending}
        />
      )}

      {(() => {
        const editing = receptions?.find((r) => r.id === editPlantReceptionId);
        if (!editing) return null;
        return (
          <EditReceptionPlantDialog
            open={!!editPlantReceptionId}
            onOpenChange={(open) =>
              setEditPlantReceptionId(open ? editing.id : null)
            }
            reception={editing}
            purchaseOrderId={order.id}
          />
        );
      })()}

      {modifyRequest && (
        <RequestReceptionModificationDialog
          open={!!modifyRequest}
          onOpenChange={(open) => !open && setModifyRequest(null)}
          receptionItemId={modifyRequest.item.id}
          itemName={modifyRequest.item.name}
          unit={modifyRequest.item.unit}
          currentQuantity={modifyRequest.item.quantity}
          currentUnitCost={modifyRequest.item.unitCost}
          currentExpiryDate={modifyRequest.item.expiry}
          initialMode={modifyRequest.mode}
        />
      )}
    </PageContainer>
  );
}
